/**
 * ## Asynchronously creates a SHA-256 hash of a given string and returns it as a hexadecimal string.
 *
 * The function uses a TextEncoder to convert the input string into bytes. It then computes the hash using the
 * SubtleCrypto API (crypto.subtle.digest) with the SHA-256 algorithm. Finally, it transforms the resulting byte
 * array into a hexadecimal string.
 *
 * Alternative - library Crypto-js (23Kb Minified + Gzipped):
 * https://github.com/brix/crypto-js
 */
const hashString = async (str: string) => {
    const encoder = new TextEncoder();
    const data = encoder.encode(str);

    const hashBuffer = await crypto.subtle.digest('SHA-256', data as BufferSource);

    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
};

/**
 * ## Creates and returns an object with methods for managing cached data in localStorage.
 *
 * This function implements caching with a time-to-live (TTL) of 24 hours. It provides two methods:
 *
 * - **setLocalCache**: Saves data to localStorage under a given key. It calculates the expiration time
 *   (`staleTime`) as the current time plus the TTL. The data is stored as a JSON string with the format:
 *   `{ staleTime, data }`.
 *
 * - **getLocalCache**: Retrieves data from localStorage by the specified key. If no data is found or if
 *   the cached data has expired (i.e. the current time is greater than the stored `staleTime`), the function
 *   removes the cached item and returns `null`. Otherwise, it returns the stored data.
 *
 * The function also checks for the availability of localStorage to ensure compatibility with environments
 * (such as server-side rendering) where localStorage might not be available.
 */
export const localCache = (ttl?: number) => {
    const DEFAULT_TTL = 24 * 60 * 60 * 1000; // ttl = 24 hrs
    const timeToLive = ttl || DEFAULT_TTL;

    const setLocalCache = async <T>({ key, value }: { key: string; value: T }) => {
        // Check if it's a client side
        if (typeof localStorage === 'undefined') {
            return;
        }

        const hashedKey = await hashString(key);

        const now = Date.now();
        const staleTime = now + timeToLive;
        const payload = { staleTime, data: value };

        localStorage.setItem(hashedKey, JSON.stringify(payload));
    };

    const getLocalCache = async <T>(key: string): Promise<T | null> => {
        // Check if it's a client side
        if (typeof localStorage === 'undefined') {
            return null;
        }

        const hashedKey = await hashString(key);

        // If no cached data
        const cachedData = localStorage.getItem(hashedKey);
        if (!cachedData) {
            return null;
        }

        const { staleTime, data } = JSON.parse(cachedData) as {
            staleTime: number;
            data: T;
        };

        // If current time more than stale time
        if (Date.now() > staleTime) {
            localStorage.removeItem(hashedKey);
            return null;
        }

        return data;
    };

    return { getLocalCache, setLocalCache };
};
